// Club 2.0 — the nightclub lobby. A slow film camera drifts through a procedural
// 8-bit nightclub while every online buddy dances on the floor; lobby chat floats
// past as pixel speech bubbles. ALL of it is code: seeded pixel paint on one
// full-bleed canvas whose internal resolution follows the on-screen region
// (integer pixel scale, no letterbox; ~11fps, ~3ms budget) and three ~60s tracks
// synthesized live in WebAudio — zero media files. Audio starts MUTED (browser
// autoplay law): the statusbar music pill's unmute click is the gesture that
// wakes the engine. The jukebox itself is app-global (exported `music`) and so
// are ALL its controls — mute + track lock live on the pill, not in here.
import { h } from 'preact';
import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import htm from 'htm';
import { hashString, mulberry32, spriteFor, paintSprite, petSeedFor, SPRITE_W, SPRITE_H } from './pet.js';
import { nameColor, EMOTES } from './chat.js';
import { brandGrid } from './brand.js';

const html = htm.bind(h);

const BASE_W = 272; // reference internal width — every focal length is tuned to it
const TICK_MS = 90; // ~11fps — dance energy, still proudly steppy

// ---------------------------------------------------------------------------
// THE MUSIC — three ~60s tracks on one endless transport.
// The transport is pure math over performance.now(): beatPhase drives the lights
// even while muted, and audio (when enabled) is scheduled TO the transport — so
// mutes, tab hops and the table backdrop all stay seamlessly on the same song.
// A lock pins the transport to one track looping alone (the statusbar pill's ⟳/🔒).
// ---------------------------------------------------------------------------

const midiHz = (n) => 440 * 2 ** ((n - 69) / 12);

// the four-band ceiling wash, top to bottom — hoisted out of the frame loop
const WASH_ALPHA = [0.1, 0.08, 0.0625, 0.05];

const MOON_ROOT = [45, 41, 48, 43]; // Am F C G — the 2am progression
const MOON_3RD = [3, 4, 4, 4];

// velvet: a ii-V-I-vi turnaround — hand-set walking bass, voicings, and licks
const VELVET_WALK = [
  [38, 41, 45, 44], // Dm7 — D F A, chromatic drop onto G
  [43, 47, 50, 37], // G7 — G B D, chromatic push into C
  [36, 40, 43, 34], // Cmaj7 — C E G, sliding down toward A
  [33, 36, 40, 39], // Am7 — A C E, walking back up to D
];
const VELVET_CHORD = [
  [50, 53, 57, 60], // D F A C
  [47, 50, 53, 55], // B D F G
  [48, 52, 55, 59], // C E G B
  [48, 52, 55, 57], // C E G A
];
// [16th step, midi, beats, bend] — bend ≠ 1 ramps the pitch across the note
const VELVET_LICKS = [
  [[0, 76, 0.5, 1], [2, 74, 0.4, 1], [4, 72, 0.9, 1.12], [10, 69, 1.4, 0.94]],
  [[2, 72, 0.4, 1], [4, 74, 0.4, 1], [6, 76, 1.0, 1.13], [12, 79, 1.5, 1]],
];

// voltage: E-minor pentatonic saw riff over Em C G D, two bars long
const VOLT_ROOT = [40, 36, 43, 38];
const VOLT_3RD = [3, 4, 4, 4];
const VOLT_RIFF = [
  [0, 12], [2, 15], [4, 12], [6, 19], [8, 15], [10, 12], [12, 10], [14, 12],
  [16, 7], [18, 10], [20, 12], [22, 15], [24, 12], [26, 10], [28, 7], [30, 3],
];

const TRACKS = [
  {
    id: 'moonlit', bpm: 92, bars: 24, energy: 0.35,
    kick: '1.......1.......', kickVol: 0.2,
    snare: '................', snareVol: 0, snareHz: 2000,
    hat: '....1.......1..1', hatVol: 0.035, hatDur: 0.1, hatHz: 5800, hatType: 'bandpass',
    notes(bar, st) {
      const out = [];
      const root = MOON_ROOT[bar % 4];
      if (st === 0 || st === 10)
        out.push({ n: root, durB: st ? 0.8 : 1.6, vol: 0.17, type: 'triangle', send: 0.05 });
      if (st % 2 === 0) {
        // soft pulse arps floating on the delay — the warm heart of the track
        const tones = [0, 7, 12, 12 + MOON_3RD[bar % 4]];
        out.push({ n: root + 12 + tones[(st >> 1) % 4], durB: 0.45, vol: 0.042, type: 'square', send: 0.5 });
      }
      if (bar % 2 === 1) {
        const hit = { 0: 36, 6: 31, 12: 24 + MOON_3RD[bar % 4] }[st];
        if (hit != null) out.push({ n: root + hit, durB: 1.4, vol: 0.05, type: 'triangle', send: 0.6 });
      }
      return out;
    },
  },
  {
    // ~110 BPM jazz: shuffled 8ths, walking bass, brushed snare, licks that bend
    id: 'velvet', bpm: 110, bars: 28, energy: 0.55, swing: 0.42,
    kick: '1.....2.1.....1.', kickVol: 0.22, // 1, ghost on the and-of-2, 3, push into the next bar
    snare: '....1..2....1..2', snareVol: 0.085, snareHz: 3400, snareDur: 0.16, // brushes on 2/4 + ghost drags
    hat: '1...1.1.1...1.1.', hatVol: 0.042, hatDur: 0.09, hatHz: 7200, hatType: 'bandpass', // the ride
    notes(bar, st) {
      const out = [];
      const ch = bar % 4;
      if (st % 4 === 0)
        out.push({ n: VELVET_WALK[ch][st >> 2], durB: 0.9, vol: 0.24, type: 'triangle', send: 0.03 });
      // the octave pop on the and-of-4 — the little kick in the walk's step
      if (st === 14)
        out.push({ n: VELVET_WALK[ch][3] + 12, durB: 0.28, vol: 0.13, type: 'triangle', send: 0.08 });
      if (st === 0 || st === 6)
        for (const c of VELVET_CHORD[ch])
          out.push({ n: c, durB: st ? 1.1 : 1.7, vol: 0.028, type: 'triangle', send: 0.4 });
      if (bar % 4 === 3) {
        // every fourth bar somebody takes a little solo
        const lick = VELVET_LICKS[(bar >> 2) % VELVET_LICKS.length];
        const hit = lick.find((l) => l[0] === st);
        if (hit)
          out.push({ n: hit[1], durB: hit[2], vol: 0.05, type: 'square', send: 0.45,
            slide: hit[3] === 1 ? undefined : hit[3] });
      }
      return out;
    },
  },
  {
    // ~132 BPM EDM: four-on-the-floor, offbeat opens, pumping pads, riser → drop
    // 8-bar phrase: bars 0-3 drop (saw-stack lead), 4-5 groove, 6 sparse, 7 riser
    id: 'voltage', bpm: 132, bars: 32, energy: 1, crash: true,
    kick: (bar) => (bar % 8 === 6 ? '1.......1.......' : bar % 8 === 7 ? '................' : '1...1...1...1...'),
    kickVol: 0.42,
    snare: (bar) => (bar % 8 === 7 ? '1...1...1.1.1.1.' : '....1.......1...'),
    snareVol: 0.13, snareHz: 1800, snareDur: 0.1,
    hat: '..1...1...1...1.', hatVol: 0.05, hatDur: 0.13, hatHz: 8200, hatType: 'highpass', // offbeat opens
    riser: true,
    notes(bar, st) {
      const out = [];
      const ph = bar % 8;
      const root = VOLT_ROOT[bar % 4];
      if (st % 4 === 0)
        // sidechain-style pads: each swells up between the kicks (env: 'pump')
        for (const iv of [0, 7, 12, 12 + VOLT_3RD[bar % 4]])
          out.push({ n: root + iv, durB: 0.96, vol: ph >= 6 ? 0.05 : 0.04, type: 'sawtooth', send: 0.12, env: 'pump' });
      if (st % 4 === 2 && ph < 6)
        out.push({ n: root, durB: 0.35, vol: 0.16, type: 'triangle', send: 0.02 });
      if (ph <= 3 && st % 2 === 0) {
        // the drop: detuned dual-saw lead riding the riff
        const key = (bar % 2) * 16 + st;
        const hit = VOLT_RIFF.find((m) => m[0] === key);
        if (hit) out.push({ n: 64 + hit[1], durB: 0.3, vol: 0.048, type: 'sawtooth', send: 0.18, stack: 14 });
      }
      return out;
    },
  },
];

const durOf = (tr) => (tr.bars * 240) / tr.bpm;
const CYCLE_S = TRACKS.reduce((a, t) => a + durOf(t), 0);
const CYCLE_BARS = TRACKS.reduce((a, t) => a + t.bars, 0);

const readLock = () => {
  try {
    const v = localStorage.getItem('vb:club:track');
    return TRACKS.some((t) => t.id === v) ? v : null;
  } catch {
    return null;
  }
};

const readMuted = () => {
  try {
    return (localStorage.getItem('vb:club:muted') ?? '1') === '1';
  } catch {
    return true;
  }
};

export const music = {
  t0: performance.now() / 1000,
  ctx: null, master: null, delayIn: null, noiseBuf: null,
  timer: null, anchor: null, schedPos: 0,
  lock: readLock(), // track id pinned by the statusbar pill, or null for the auto rotation
  muted: readMuted(),
  subs: new Set(), // onChange listeners — mute/lock flips from any tab
  beatSubs: new Set(), beatTimer: null, lastBeat: null,

  now() { return performance.now() / 1000 - this.t0; },

  setLock(id) {
    this.lock = id === 'auto' ? null : id;
    this.anchor = null; // re-quantize the scheduler onto the new song
    try { localStorage.setItem('vb:club:track', this.lock ?? 'auto'); } catch {}
    this.emit();
  },

  // ---- the global face: the statusbar pill and the lobby share this jukebox ----

  start() { this.setAudible(!this.muted); },

  toggleMute() {
    this.muted = !this.muted;
    try { localStorage.setItem('vb:club:muted', this.muted ? '1' : '0'); } catch {}
    this.setAudible(!this.muted); // inside the click — the gesture the autoplay policy wants
    this.syncBeat();
    this.emit();
  },

  state() {
    const id = this.info().track.id;
    return { muted: this.muted, track: id, trackName: id, mode: this.lock ? 'lock' : 'auto', playing: !this.muted && !!this.timer };
  },

  onChange(cb) {
    this.subs.add(cb);
    return () => this.subs.delete(cb);
  },

  emit() { for (const cb of [...this.subs]) cb(); },

  onBeat(cb) {
    this.beatSubs.add(cb);
    this.syncBeat();
    return () => {
      this.beatSubs.delete(cb);
      this.syncBeat();
    };
  },

  // the beat ticker only spins while someone listens AND sound is on AND the
  // tab is visible — zero background work otherwise
  syncBeat() {
    const want = this.beatSubs.size > 0 && !this.muted && !document.hidden;
    if (want && !this.beatTimer) {
      this.lastBeat = null;
      this.beatTimer = setInterval(() => {
        const i = this.info();
        const key = `${i.idx}:${i.beat}`;
        if (key === this.lastBeat) return;
        this.lastBeat = key;
        for (const cb of [...this.beatSubs]) cb();
      }, TICK_MS);
    } else if (!want && this.beatTimer) {
      clearInterval(this.beatTimer);
      this.beatTimer = null;
    }
  },

  info(tt = this.now()) {
    const li = this.lock ? TRACKS.findIndex((t) => t.id === this.lock) : -1;
    if (li >= 0) {
      // locked: one track loops alone on the same clock
      const tr = TRACKS[li];
      const d = durOf(tr);
      const pos = ((tt % d) + d) % d;
      const beatF = (pos * tr.bpm) / 60;
      return {
        track: tr, idx: li, beatF,
        beat: Math.floor(beatF), beatPhase: beatF % 1,
        bar: Math.floor(beatF / 4), barPhase: (beatF / 4) % 1,
        gbar: Math.floor(tt / d) * tr.bars + Math.floor(beatF / 4),
        energy: tr.energy,
      };
    }
    let pos = ((tt % CYCLE_S) + CYCLE_S) % CYCLE_S;
    const cycles = Math.floor(tt / CYCLE_S);
    let i = 0;
    let barsBefore = 0;
    while (i < TRACKS.length - 1 && pos >= durOf(TRACKS[i]) - 1e-9) {
      pos -= durOf(TRACKS[i]);
      barsBefore += TRACKS[i].bars;
      i++;
    }
    const tr = TRACKS[i];
    const beatF = (pos * tr.bpm) / 60;
    return {
      track: tr, idx: i, beatF,
      beat: Math.floor(beatF), beatPhase: beatF % 1,
      bar: Math.floor(beatF / 4), barPhase: (beatF / 4) % 1,
      gbar: cycles * CYCLE_BARS + barsBefore + Math.floor(beatF / 4),
      energy: tr.energy,
    };
  },

  build() {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return;
    const ctx = (this.ctx = new C());
    this.master = ctx.createGain();
    this.master.gain.value = 0.0001;
    this.master.connect(ctx.destination);
    // light "reverb": one feedback delay line fed by per-voice sends
    this.delayIn = ctx.createDelay(0.6);
    this.delayIn.delayTime.value = 0.28;
    const fb = ctx.createGain();
    fb.gain.value = 0.34;
    this.delayIn.connect(fb);
    fb.connect(this.delayIn);
    const wet = ctx.createGain();
    wet.gain.value = 0.4;
    this.delayIn.connect(wet);
    wet.connect(this.master);
    const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
  },

  setAudible(on) {
    if (on) {
      if (!this.ctx) this.build();
      if (!this.ctx) return;
      if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
      if (this.ctx.state !== 'running') {
        // page loaded pre-unmuted: the next click anywhere becomes the gesture
        const kick = () => this.ctx?.resume().catch(() => {});
        document.addEventListener('pointerdown', kick, { once: true });
      }
      this.master.gain.setTargetAtTime(0.75, this.ctx.currentTime, 0.05);
      if (!this.timer) {
        this.anchor = null;
        this.timer = setInterval(() => this.pump(), 80);
        this.pump();
      }
    } else {
      if (this.ctx && this.master) this.master.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.03);
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      this.anchor = null;
    }
  },

  quantUp(tt) {
    const tp = this.info(tt);
    const stepLen = 60 / tp.track.bpm / 4;
    const stepsIn = tp.beatF * 4;
    return tt + (Math.ceil(stepsIn - 1e-3) - stepsIn) * stepLen;
  },

  pump() {
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state !== 'running') {
      this.anchor = null; // a frozen clock must never bank a burst of backlog
      return;
    }
    const tnow = this.now();
    if (!this.anchor || this.schedPos < tnow - 0.1) {
      this.anchor = { at: ctx.currentTime + 0.06, tt: tnow };
      this.schedPos = this.quantUp(tnow);
    }
    const ahead = tnow + 0.35;
    let guard = 0;
    while (this.schedPos < ahead && guard++ < 64) {
      const tp = this.info(this.schedPos + 1e-4);
      const stepLen = 60 / tp.track.bpm / 4;
      const stepIdx = Math.round(tp.beatF * 4);
      const at = this.anchor.at + (this.schedPos - this.anchor.tt);
      if (at > ctx.currentTime - 0.02) {
        // swing: the offbeat 8ths land late — that's the shuffle
        const sw = tp.track.swing && stepIdx % 4 === 2 ? tp.track.swing * (30 / tp.track.bpm) : 0;
        this.step(tp.track, Math.floor(stepIdx / 16), stepIdx % 16, Math.max(at + sw, ctx.currentTime + 0.005));
      }
      this.schedPos += stepLen;
    }
  },

  step(tr, bar, st, at) {
    const pat = (p) => (typeof p === 'function' ? p(bar) : p);
    // '1' = full hit, '2' = ghost note at half strength — the groove lives in the ghosts
    const k = pat(tr.kick)[st];
    if (k === '1' || k === '2') this.kick(at, k === '2' ? tr.kickVol * 0.45 : tr.kickVol);
    const hp = pat(tr.hat);
    if (hp[st] === '1')
      this.noise(at, tr.hatDur, tr.hatAcc && st % 4 === 0 ? tr.hatAcc : tr.hatVol, tr.hatHz, tr.hatType);
    const s = pat(tr.snare)[st];
    if (s === '1' || s === '2')
      this.noise(at, tr.snareDur ?? 0.09, s === '2' ? tr.snareVol * 0.4 : tr.snareVol, tr.snareHz, 'bandpass');
    if (tr.crash && st === 0 && bar % 8 === 0) this.noise(at, 0.7, 0.09, 5200, 'highpass'); // the drop lands
    for (const nt of tr.notes(bar, st)) this.tone(at, midiHz(nt.n), (nt.durB ?? 0.5) * (60 / tr.bpm), nt);
    if (tr.riser && st === 0 && bar % 8 === 7) this.sweep(at, 240 / tr.bpm);
  },

  tone(at, hz, dur, nt) {
    const ctx = this.ctx;
    const vol = nt.vol ?? 0.08;
    const g = ctx.createGain();
    if (nt.env === 'pump') {
      // sidechain feel: the note ducks under the kick and swells back up
      g.gain.setValueAtTime(0.0001, at);
      g.gain.linearRampToValueAtTime(Math.max(0.001, vol), at + dur * 0.55);
      g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    } else {
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(Math.max(0.001, vol), at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    }
    g.connect(this.master);
    const send = nt.send ?? 0.15;
    if (send > 0.01) {
      const s = ctx.createGain();
      s.gain.value = send;
      g.connect(s);
      s.connect(this.delayIn);
    }
    const mk = (cents) => {
      const o = ctx.createOscillator();
      o.type = nt.type ?? 'square';
      o.frequency.setValueAtTime(hz, at);
      if (cents) o.detune.setValueAtTime(cents, at);
      if (nt.slide) o.frequency.exponentialRampToValueAtTime(Math.max(24, hz * nt.slide), at + dur);
      o.connect(g);
      o.start(at);
      o.stop(at + dur + 0.05);
    };
    if (nt.stack) {
      mk(-nt.stack); // the saw stack: two oscillators leaning apart
      mk(nt.stack);
    } else mk(0);
  },

  kick(at, vol) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(150, at);
    o.frequency.exponentialRampToValueAtTime(42, at + 0.12);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, at);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
    o.connect(g);
    g.connect(this.master);
    o.start(at);
    o.stop(at + 0.2);
  },

  noise(at, dur, vol, hz, type) {
    if (!vol) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = hz;
    f.Q.value = 0.9;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, at);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.master);
    src.start(at);
    src.stop(at + dur + 0.05);
  },

  sweep(at, dur) {
    // the riser: one bar of noise climbing the filter into the drop
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.setValueAtTime(300, at);
    f.frequency.exponentialRampToValueAtTime(5500, at + dur);
    f.Q.value = 1.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, at);
    g.gain.linearRampToValueAtTime(0.11, at + dur * 0.9);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(f);
    f.connect(g);
    g.connect(this.master);
    src.start(at);
    src.stop(at + dur + 0.05);
  },
};

document.addEventListener('visibilitychange', () => music.syncBeat());

// ---------------------------------------------------------------------------
// THE ROOM — world units: floor is z=0, the LED wall stands at y=WALL_Y, z up.
// Since 2.0 the floor lives in an actual room: side walls at ±SIDE_X, a front
// wall at FRONT_Y, a bar, booth seats, and an EXIT door somebody always ignores.
// ---------------------------------------------------------------------------

const FX0 = -6, FX1 = 6, FY0 = -5, FY1 = 5; // dance floor, 12x10 tiles
const WALL_Y = -6.9;
const SIDE_X = 8.6;
const FRONT_Y = 7.5;
const LEDQ = { x0: -5.0, x1: 5.0, z0: 1.0, z1: 4.4 };
const LW = 24, LH = 12; // LED wall resolution
const BOOTH = { x0: -2.1, x1: 2.1, y: -5.3, z: 1.1, top: 0.8 };
const BALL = [0, -0.5, 3.55];
const DJ_SEED = hashString('vibebuddy:club:dj'); // only if the room is truly empty
const BEAM_COLORS = ['#35d07f', '#f5c542', '#6fb7e8', '#ff5f7a'];
const NEON = ['#35d07f', '#f5c542', '#6fb7e8', '#ff5f7a', '#b18aff'];
// twenty dance spots, owner-agnostic; per-user jitter keeps the crowd organic
const SPOTS = [
  [-4.6, -3.7], [-1.6, -3.3], [1.4, -3.6], [4.4, -3.2],
  [-3.2, -2.2], [0.2, -2.5], [3.3, -2.0],
  [-4.7, -0.9], [-1.7, -0.7], [1.6, -1.1], [4.6, -0.6],
  [-3.3, 0.6], [0.1, 0.9], [3.2, 0.5],
  [-4.5, 2.1], [-1.5, 2.4], [1.7, 1.9], [4.5, 2.3],
  [-2.4, 3.6], [2.0, 3.8],
];
// 卡座 — booth seats hugging the walls; s = which wall they lean on
const SEATS = [
  { x: 7.65, y: -2.4, s: 1 },
  { x: 7.65, y: 1.6, s: 1 },
  { x: -7.65, y: 4.0, s: -1 },
];

const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a, b, t) => a + (b - a) * t;
const lerp3 = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

// free look-at camera (unlike the garden's orbit rig — the director needs dollies)
function makeCam(eye, tgt, focal, W, H) {
  let fx = tgt[0] - eye[0], fy = tgt[1] - eye[1], fz = tgt[2] - eye[2];
  const fl = Math.hypot(fx, fy, fz) || 1;
  fx /= fl; fy /= fl; fz /= fl;
  let rx = fy, ry = -fx; // cross(f, up), up = +z
  const rl = Math.hypot(rx, ry) || 1;
  rx /= rl; ry /= rl;
  const ux = ry * fz, uy = -rx * fz, uz = rx * fy - ry * fx; // cross(r, f)
  const cy = H * 0.54;
  return (x, y, z) => {
    const vx = x - eye[0], vy = y - eye[1], vz = z - eye[2];
    const d = vx * fx + vy * fy + vz * fz;
    if (d < 0.35) return null;
    return [W / 2 + (focal * (vx * rx + vy * ry)) / d, cy - (focal * (vx * ux + vy * uy + vz * uz)) / d, d];
  };
}

// ---- the camera director: eased keyframes, cuts only on bar boundaries.
// no overhead shots anymore — the venue has no ceiling and nobody needs to know.

const SHOT_KINDS = ['orbit', 'push', 'dj', 'crowd', 'crowd', 'rack'];

function buildShot(kind, rnd, w) {
  const dancers = [...w.dancers.values()];
  if (kind === 'crowd' && !dancers.length) kind = 'orbit';
  switch (kind) {
    case 'push': {
      // low-angle push-in toward the LED wall
      const x0 = (rnd() - 0.5) * 5;
      const A = [x0, 5.8, 0.7], B = [x0 * 0.25, 0.8, 1.5];
      return { dur: 14 + rnd() * 4, at: (e) => ({ eye: lerp3(A, B, e), tgt: [0, WALL_Y, 2.5], focal: lerp(180, 235, e) }) };
    }
    case 'dj': {
      // slow dolly past the booth, eyes on the DJ up on the riser
      const s = rnd() < 0.5 ? 1 : -1;
      const A = [-3.2 * s, -1.6, 1.7], B = [3.2 * s, -2.4, 1.4];
      return { dur: 13 + rnd() * 3, at: (e) => ({ eye: lerp3(A, B, e), tgt: [0, -6.1, 1.6], focal: 300 }) };
    }
    case 'crowd': {
      // pick one dancer, arc slowly around them — the name stays readable
      const d = dancers[Math.floor(rnd() * dancers.length)];
      const a0 = rnd() * Math.PI * 2;
      const dA = (rnd() < 0.5 ? -1 : 1) * (0.9 + rnd() * 0.5);
      const r = 2.6;
      return {
        dur: 12 + rnd() * 3,
        at: (e) => {
          const a = a0 + dA * e;
          return {
            eye: [d.x + Math.cos(a) * r, Math.max(-4.6, d.y + Math.sin(a) * r), lerp(1.2, 1.45, e)],
            tgt: [d.x, d.y, 0.85], focal: 320,
          };
        },
      };
    }
    case 'rack': {
      // rack from the disco ball down to the floor — stays shallow, never a god view
      const A = [(rnd() - 0.5) * 3, 6.2, 2.7];
      return {
        dur: 13 + rnd() * 3,
        at: (e) => ({ eye: [A[0], lerp(6.2, 5.4, e), A[2]], tgt: lerp3([0, -0.5, 3.4], [0, -0.5, 0.8], e), focal: 260 }),
      };
    }
    default: {
      // slow orbit of the floor, kept inside the walls and off the ceiling
      const a0 = rnd() * Math.PI * 2;
      const dA = (rnd() < 0.5 ? -1 : 1) * (0.9 + rnd() * 0.6);
      const rx = 6.4 + rnd() * 1.6, ry = 3.4 + rnd() * 1.1, z = 2.2 + rnd() * 1.3;
      return {
        dur: 18 + rnd() * 7,
        at: (e) => {
          const a = a0 + dA * e;
          return { eye: [Math.sin(a) * rx, 2 + Math.cos(a) * ry, z], tgt: [0, -1.2, 1.3], focal: 210 };
        },
      };
    }
  }
}

function nextShot(w, tt) {
  if (!w.shotQueue.length) {
    w.shotEpoch++;
    const rnd = mulberry32((w.seed ^ Math.imul(w.shotEpoch, 0x9e3779b1)) >>> 0);
    w.shotQueue = SHOT_KINDS.slice();
    for (let i = w.shotQueue.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [w.shotQueue[i], w.shotQueue[j]] = [w.shotQueue[j], w.shotQueue[i]];
    }
    w.shotRnd = rnd;
  }
  w.shot = buildShot(w.shotQueue.shift(), w.shotRnd, w);
  w.shotStart = tt;
}

// ---- sprite cache: dancers repaint ~1/s (blinks, shimmer) not every frame ----

const SPR_CACHE = new Map();
// outfits arrive async (batch endpoint) — the cache key carries them so a
// freshly dressed dancer repaints on the next sprite refresh
function outfitKey(w, name) {
  const eq = w?.outfits?.[name];
  return eq ? `${name}:${eq.hat ?? ''}.${eq.shoes ?? ''}.${eq.necklace ?? ''}` : name;
}
function spriteEntry(key, seed, frame, phones, equip) {
  let e = SPR_CACHE.get(key);
  if (!e) {
    if (SPR_CACHE.size > 48) SPR_CACHE.delete(SPR_CACHE.keys().next().value);
    const mk = () => {
      const cv = document.createElement('canvas');
      cv.width = SPRITE_W * 2;
      cv.height = SPRITE_H * 2;
      return cv;
    };
    e = { cv: mk(), flip: mk(), drawn: -99 };
    SPR_CACHE.set(key, e);
  }
  if (frame - e.drawn > 10) {
    e.drawn = frame;
    const ctx = e.cv.getContext('2d');
    ctx.clearRect(0, 0, e.cv.width, e.cv.height);
    paintSprite(ctx, spriteFor(seed, { equip }), 0, 0, 2, { frame, blink: frame % 9 === 8 });
    if (phones) {
      // headphones — the DJ badge: band over the crown, cups on the ears
      ctx.fillStyle = '#221f38';
      ctx.fillRect(4, 2, 16, 3);
      ctx.fillStyle = '#16151f';
      ctx.fillRect(2, 5, 4, 7);
      ctx.fillRect(18, 5, 4, 7);
      ctx.fillStyle = '#f5c542';
      ctx.fillRect(3, 8, 2, 2);
      ctx.fillRect(19, 8, 2, 2);
    }
    const f = e.flip.getContext('2d');
    f.clearRect(0, 0, e.flip.width, e.flip.height);
    f.save();
    f.translate(e.flip.width, 0);
    f.scale(-1, 1);
    f.drawImage(e.cv, 0, 0);
    f.restore();
  }
  return e;
}

// ---- the LED wall painter: one color per cell, moods keyed off track energy ----

const BRAND8 = (() => {
  const g = brandGrid();
  return Array.from({ length: 8 }, (_, y) =>
    Array.from({ length: 8 }, (_, x) => g[y * 2][x * 2] || g[y * 2 + 1][x * 2 + 1] || null)
  );
})();

const DIGITS = {
  1: ['.1.', '11.', '.1.', '.1.', '111'],
  2: ['11.', '..1', '.1.', '1..', '111'],
  3: ['111', '..1', '.11', '..1', '111'],
  4: ['1.1', '1.1', '111', '..1', '..1'],
};

function paintLED(buf, info, tt, frame) {
  const e = info.energy;
  if (e < 0.5) {
    // moonlit: a drifting starfield
    buf.fill('#0d1124');
    const rnd = mulberry32(0x57a99);
    for (let i = 0; i < 30; i++) {
      const sx = rnd() * LW, sy = rnd() * LH, sp = 0.3 + rnd() * 1.2;
      if ((frame + i * 3) % 16 >= 11) continue;
      const x = Math.floor((sx + tt * sp) % LW);
      buf[Math.floor(sy) * LW + x] = i % 6 === 0 ? '#f2e6b8' : i % 2 ? '#8fa3ea' : '#dfe6ff';
    }
    if (info.bar % 4 === 0) {
      const cx = Math.floor(info.barPhase * (LW + 10)) - 5;
      const cy = 2 + (info.bar % 3);
      for (let i = 0; i < 4; i++)
        if (cx - i >= 0 && cx - i < LW) buf[cy * LW + cx - i] = i ? '#4a5488' : '#fff6d8';
    }
  } else if (e < 0.9) {
    // velvet: smoke curling in slow motion, a few stars breathing through it
    for (let y = 0; y < LH; y++)
      for (let x = 0; x < LW; x++) {
        const v =
          Math.sin(x * 0.52 + tt * 0.5 + Math.sin(y * 0.85 + tt * 0.3) * 1.5) +
          Math.sin((x + y * 1.7) * 0.33 - tt * 0.36);
        buf[y * LW + x] = v > 1.15 ? '#4a3f6e' : v > 0.35 ? '#2c2747' : v > -0.55 ? '#1b1830' : '#120f22';
      }
    const rnd = mulberry32(0x5e1e7);
    for (let i = 0; i < 8; i++) {
      const sx = Math.floor(rnd() * LW), sy = Math.floor(rnd() * LH);
      if ((frame + i * 5) % 24 < 10) buf[sy * LW + sx] = i % 3 ? '#8fa3ea' : '#f2e6b8';
    }
    if (info.bar % 2 === 0) {
      // one lazy spotlight sweep gliding across the panel
      const cx = Math.floor(info.barPhase * LW);
      for (let y = 0; y < LH; y++) {
        buf[y * LW + cx] = '#57517f';
        if (cx > 0) buf[y * LW + cx - 1] = '#3d3763';
      }
    }
  } else {
    // voltage: glitch bars + a rolling scanline + the count slamming every beat
    buf.fill('#120f1e');
    const rnd = mulberry32(Math.imul(frame, 2654435761) >>> 0);
    for (let i = 0; i < 9; i++) {
      const y = Math.floor(rnd() * LH);
      const x0 = Math.floor(rnd() * LW);
      const len = 2 + Math.floor(rnd() * 10);
      const c = ['#35d07f', '#f5c542', '#ff5f7a', '#dfe6ff'][Math.floor(rnd() * 4)];
      for (let x = x0; x < Math.min(LW, x0 + len); x++) buf[y * LW + x] = c;
    }
    const sy = Math.floor((tt * 7) % LH);
    for (let x = 0; x < LW; x++) if ((x + frame) % 3) buf[sy * LW + x] = '#4a4480';
    if (info.beatPhase < 0.3) {
      const dg = DIGITS[(info.beat % 4) + 1];
      const col = info.beat % 2 ? '#fff6d8' : '#f5c542';
      for (let y = 0; y < 5; y++)
        for (let x = 0; x < 3; x++)
          if (dg[y][x] === '1')
            for (let sy2 = 0; sy2 < 2; sy2++)
              for (let sx2 = 0; sx2 < 2; sx2++) buf[(1 + y * 2 + sy2) * LW + (9 + x * 2 + sx2)] = col;
    }
    if (info.bar % 8 === 0 && info.barPhase < 0.1) {
      // the drop: the whole panel whites out for a breath
      for (let i = 0; i < buf.length; i++) if (((i * 2654435761) >>> 8) % 5 < 3) buf[i] = '#e8e6ff';
    }
  }
}

// batched quad fills: one beginPath+fill per color, not per cell
function flushQuads(ctx, batches) {
  for (const [color, quads] of batches) {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (const [a, b, c, d] of quads) {
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
      ctx.lineTo(c[0], c[1]);
      ctx.lineTo(d[0], d[1]);
      ctx.closePath();
    }
    ctx.fill();
  }
}

function pushQuad(batches, color, a, b, c, d) {
  if (!a || !b || !c || !d) return;
  let arr = batches.get(color);
  if (!arr) batches.set(color, (arr = []));
  arr.push([a, b, c, d]);
}

// ---- the world pixel unit ----
// ONE atom for the whole scene: `px` (= round(FS), one chunky internal-canvas
// pixel — what a mid-floor dancer's sprite texel resolves to is 2·px). every
// hand-drawn shape below snaps to integer multiples of it; soft shapes band at
// >=2·px so nothing out-smooths the crowd. no arcs, no gradients, no glides.
const qa = (a) => Math.round(a * 16) / 16; // quantized alpha — fades step, never glide
// stepped scanline ellipse on an s-sized grid — hard bands, zero allocation
function pixelEllipse(ctx, cx, cy, rx, ry, s, dither) {
  cx = Math.round(cx / s) * s;
  cy = Math.round(cy / s) * s;
  const rows = Math.max(1, Math.round(ry / s));
  for (let gy = -rows; gy < rows; gy++) {
    const t = (gy + 0.5) / rows;
    let hw = Math.floor((rx * Math.sqrt(Math.max(0, 1 - t * t))) / s) * s;
    if (dither && (gy & 1)) hw -= s; // serrated odd rows — poor man's ordered dither
    if (hw < s) continue;
    ctx.fillRect(cx - hw, cy + gy * s, hw * 2, s);
  }
}

// ---------------------------------------------------------------------------
// THE FRAME — one full repaint per tick; measured, budget ~3ms at 2x internal
// ---------------------------------------------------------------------------

function drawClub(cv, w, backdrop) {
  const ctx = cv.getContext('2d');
  const W = cv.width, H = cv.height;
  const FS = W / BASE_W; // the whole composition scales with the internal resolution
  const px = Math.max(1, Math.round(FS)); // one chunky pixel in device units
  const tt = music.now();
  const info = music.info(tt);
  const frame = w.frame++;
  const energy = info.energy;
  const pulse = Math.max(0, 1 - info.beatPhase * 3);

  // director: hold the last pose past dur, cut the moment a bar line crosses
  if (!w.shot) nextShot(w, tt);
  if (tt - w.shotStart >= w.shot.dur && w.lastGbar !== -1 && info.gbar !== w.lastGbar) nextShot(w, tt);
  w.lastGbar = info.gbar;
  const u = Math.min(1, (tt - w.shotStart) / w.shot.dur);
  const pose = w.shot.at(easeInOut(u));
  {
    // no ceiling up there — clamp pitch (~35°) so no shot looks down into the void
    const hd = Math.hypot(pose.tgt[0] - pose.eye[0], pose.tgt[1] - pose.eye[1]) || 0.001;
    const maxDrop = hd * 0.7;
    if (pose.eye[2] - pose.tgt[2] > maxDrop) pose.tgt = [pose.tgt[0], pose.tgt[1], pose.eye[2] - maxDrop];
  }
  const focal = pose.focal * FS;
  const P = makeCam(pose.eye, pose.tgt, focal, W, H);

  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#0b0a12';
  ctx.fillRect(0, 0, W, H);

  // beams are planned up front: their landing pools go UNDER the crowd,
  // the light cones themselves go over it — same endpoint, same color
  const beams = [];
  if (energy >= 0.9) {
    const rigX = [-3.6, -1.2, 1.2, 3.6];
    for (let i = 0; i < 4; i++)
      beams.push({
        x: rigX[i],
        fx: rigX[i] + Math.sin(tt * 1.3 + i * 1.7) * 2.7,
        fy: -1 + Math.cos(tt * 0.6 + i) * 2.5,
        w: 1.35, color: BEAM_COLORS[i], alpha: 0.1 + pulse * 0.07,
      });
  } else if (energy >= 0.5) {
    // velvet: smoky slow sweeps
    const rigX = [-2.4, 0, 2.4];
    const cols = ['#b18aff', '#f5c542', '#ff8faf'];
    for (let i = 0; i < 3; i++)
      beams.push({
        x: rigX[i],
        fx: rigX[i] + Math.sin(tt * 0.22 + i * 2.1) * 3.1,
        fy: -0.4 + Math.cos(tt * 0.17 + i * 1.3) * 2.7,
        w: 1.5, color: cols[i], alpha: 0.075,
      });
  } else {
    // moonlit: two pale lazy beams
    for (let i = 0; i < 2; i++)
      beams.push({
        x: i ? 2.2 : -2.2,
        fx: (i ? 2.2 : -2.2) + Math.sin(tt * 0.12 + i * 2.6) * 2.6,
        fy: -0.6 + Math.cos(tt * 0.1 + i * 1.9) * 2.2,
        w: 1.2, color: i ? '#fff3c4' : '#8fd8ff', alpha: 0.05,
      });
  }

  // ---- back wall + LED panel ----
  {
    const wq = [P(-SIDE_X, WALL_Y, 0), P(SIDE_X, WALL_Y, 0), P(SIDE_X, WALL_Y, 4.8), P(-SIDE_X, WALL_Y, 4.8)];
    if (wq.every(Boolean)) {
      ctx.fillStyle = '#141222';
      ctx.beginPath();
      ctx.moveTo(wq[0][0], wq[0][1]);
      for (let i = 1; i < 4; i++) ctx.lineTo(wq[i][0], wq[i][1]);
      ctx.closePath();
      ctx.fill();
    }
    // neon strips framing the LED panel
    for (let i = 0; i < 4; i++) {
      const x = [-6.3, -7.5, 6.3, 7.5][i];
      const a = P(x, WALL_Y + 0.01, 0.6), b = P(x, WALL_Y + 0.01, 4.3);
      if (!a || !b) continue;
      ctx.globalAlpha = qa(0.5 + pulse * 0.3);
      ctx.strokeStyle = NEON[i % NEON.length];
      ctx.lineWidth = px;
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
  paintLED(w.led, info, tt, frame);
  {
    // project the LED lattice once, then batch cells by color
    const nx = LW + 1, nz = LH + 1;
    const pts = new Array(nx * nz);
    for (let r = 0; r <= LH; r++) {
      const z = LEDQ.z1 - ((LEDQ.z1 - LEDQ.z0) * r) / LH;
      for (let c = 0; c <= LW; c++)
        pts[r * nx + c] = P(LEDQ.x0 + ((LEDQ.x1 - LEDQ.x0) * c) / LW, WALL_Y + 0.02, z);
    }
    const batches = new Map();
    for (let r = 0; r < LH; r++)
      for (let c = 0; c < LW; c++)
        pushQuad(batches, w.led[r * LW + c], pts[r * nx + c], pts[r * nx + c + 1], pts[(r + 1) * nx + c + 1], pts[(r + 1) * nx + c]);
    flushQuads(ctx, batches);
  }

  // ---- side + front walls: dark panels with neon rails — it reads as a ROOM ----
  {
    const batches = new Map();
    for (const sx of [-SIDE_X, SIDE_X])
      for (let k = 0; k < 5; k++) {
        const y0 = WALL_Y + 0.1 + k * 2.86, y1 = y0 + 2.6;
        pushQuad(batches, '#151228', P(sx, y0, 0.1), P(sx, y1, 0.1), P(sx, y1, 4.6), P(sx, y0, 4.6));
      }
    for (let k = 0; k < 6; k++) {
      const x0 = -SIDE_X + (k * 2 * SIDE_X) / 6, x1 = x0 + (2 * SIDE_X) / 6;
      pushQuad(batches, '#131020', P(x0, FRONT_Y, 0), P(x1, FRONT_Y, 0), P(x1, FRONT_Y, 4.8), P(x0, FRONT_Y, 4.8));
    }
    flushQuads(ctx, batches);
    for (const sx of [-SIDE_X, SIDE_X])
      for (let k = 0; k < 5; k++) {
        const y0 = WALL_Y + 0.25 + k * 2.86, y1 = y0 + 2.3;
        const a = P(sx, y0, 2.75), b = P(sx, y1, 2.75);
        if (!a || !b) continue;
        ctx.globalAlpha = qa(0.4 + pulse * 0.2);
        ctx.strokeStyle = NEON[(k + (sx > 0 ? 2 : 0)) % NEON.length];
        ctx.lineWidth = px;
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
      }
    ctx.globalAlpha = 1;
  }

  // ---- the EXIT: a door on the front wall, glowing green like they all do ----
  {
    const dq = [P(4.6, FRONT_Y - 0.04, 0), P(5.8, FRONT_Y - 0.04, 0), P(5.8, FRONT_Y - 0.04, 2.3), P(4.6, FRONT_Y - 0.04, 2.3)];
    if (dq.every(Boolean)) {
      ctx.fillStyle = '#1c1930';
      ctx.beginPath();
      ctx.moveTo(dq[0][0], dq[0][1]);
      for (let i = 1; i < 4; i++) ctx.lineTo(dq[i][0], dq[i][1]);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = '#1d9e75';
      ctx.lineWidth = px;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    const sp = P(5.2, FRONT_Y - 0.05, 2.65);
    if (sp) {
      const fpx = Math.max(5, Math.round((focal * 0.32) / sp[2]));
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = '#35d07f';
      ctx.globalAlpha = qa(0.18);
      // green halo, dithered pixel bands — no smooth pool of light
      pixelEllipse(ctx, sp[0], sp[1], fpx * 2.4, fpx * 1.4, Math.max(px, Math.round(fpx / (3 * px)) * px || px), true);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.font = `${fpx}px Silkscreen, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = frame % 40 === 39 ? '#1d9e75' : '#35d07f';
      ctx.fillText('EXIT', sp[0], sp[1]);
    }
  }

  // ---- the light truss the beams hang from ----
  {
    const a = P(-4.8, -5.0, 4.66), b = P(4.8, -5.0, 4.66), c = P(4.8, -5.0, 4.5), d = P(-4.8, -5.0, 4.5);
    if (a && b && c && d) {
      ctx.fillStyle = '#1a1730';
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
      ctx.lineTo(c[0], c[1]);
      ctx.lineTo(d[0], d[1]);
      ctx.closePath();
      ctx.fill();
    }
    for (const bm of beams) {
      const f = P(bm.x, -5.0, 4.52);
      if (!f) continue;
      ctx.fillStyle = bm.color;
      ctx.fillRect(Math.round(f[0]) - px, Math.round(f[1]), px * 2, px * 2);
    }
  }

  // ---- apron floor: the concrete the whole room stands on ----
  {
    const batches = new Map();
    const nx = 8, ny = 6;
    for (let gy = 0; gy < ny; gy++)
      for (let gx = 0; gx < nx; gx++) {
        const x0 = -SIDE_X + (gx * 2 * SIDE_X) / nx, x1 = x0 + (2 * SIDE_X) / nx;
        const y0 = WALL_Y + (gy * (FRONT_Y - WALL_Y)) / ny, y1 = y0 + (FRONT_Y - WALL_Y) / ny;
        pushQuad(batches, (gx + gy) % 2 ? '#0e0c19' : '#100e1c', P(x0, y0, 0), P(x1, y0, 0), P(x1, y1, 0), P(x0, y1, 0));
      }
    flushQuads(ctx, batches);
  }

  // ---- the dance floor: color-cycling checker, hot tiles flash on the beat ----
  {
    const hue = Math.round((tt * 8 * (0.4 + energy)) % 360);
    const sat = energy < 0.5 ? 35 : 55;
    const colA = `hsl(${hue} ${sat}% ${Math.round(20 + pulse * (6 + 10 * energy))}%)`;
    const colB = `hsl(${(hue + 40) % 360} ${sat}% ${Math.round(11 + pulse * (3 + 6 * energy))}%)`;
    const nx = 13;
    const pts = new Array(nx * 11);
    for (let gy = 0; gy <= 10; gy++)
      for (let gx = 0; gx <= 12; gx++) pts[gy * nx + gx] = P(FX0 + gx, FY0 + gy, 0);
    const batches = new Map();
    for (let gy = 0; gy < 10; gy++)
      for (let gx = 0; gx < 12; gx++)
        pushQuad(batches, (gx + gy) % 2 ? colA : colB,
          pts[gy * nx + gx], pts[gy * nx + gx + 1], pts[(gy + 1) * nx + gx + 1], pts[(gy + 1) * nx + gx]);
    flushQuads(ctx, batches);
    if (energy >= 0.5 && pulse > 0.05) {
      // a seeded handful of tiles catches fire each beat
      const rnd = mulberry32((Math.imul(info.gbar, 31) + info.beat + 7) >>> 0);
      const hot = new Map();
      for (let i = 0; i < 5; i++) {
        const idx = Math.floor(rnd() * 120);
        const gx = idx % 12, gy = (idx / 12) | 0;
        pushQuad(hot, rnd() < 0.5 ? '#f5c542' : '#dfe6ff',
          pts[gy * nx + gx], pts[gy * nx + gx + 1], pts[(gy + 1) * nx + gx + 1], pts[(gy + 1) * nx + gx]);
      }
      ctx.globalAlpha = qa(pulse * 0.45);
      flushQuads(ctx, hot);
      ctx.globalAlpha = 1;
    }
  }

  // ---- beam landing pools: stepped pixel ellipses, same color as their beam ----
  ctx.globalCompositeOperation = 'screen';
  for (const b of beams) {
    const pe = P(b.fx, b.fy, 0);
    if (!pe) continue;
    const r = Math.max(3, (focal * b.w) / pe[2]);
    // band height grows with the pool so close-ups stay just as chunky
    const s = Math.max(2 * px, Math.ceil((r * 0.72) / (8 * px)) * px);
    ctx.fillStyle = b.color;
    ctx.globalAlpha = qa(Math.min(0.3, b.alpha * 1.1)); // wide wash — the floor takes the beam's color
    pixelEllipse(ctx, pe[0], pe[1], r * 1.9, r * 0.72, s, true);
    ctx.globalAlpha = qa(Math.min(0.55, b.alpha * 2.4));
    pixelEllipse(ctx, pe[0], pe[1], r, r * 0.38, s, false); // hot core — one hard band in
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';

  // ---- speaker stacks: two a side, cones breathing, the odd bass shudder ----
  {
    const stacks = [-4.55, -3.25, 3.25, 4.55];
    for (let ix = 0; ix < 4; ix++) {
      const sxp = stacks[ix];
      const shud =
        energy > 0.55 && pulse > 0.72 && (frame + ix) % 5 < 2 ? (((frame * 13 + ix * 7) % 3) - 1) * px : 0;
      ctx.save();
      ctx.translate(shud, 0);
      const batches = new Map();
      pushQuad(batches, '#1d1a30', P(sxp - 0.55, -5.8, 0), P(sxp + 0.55, -5.8, 0), P(sxp + 0.55, -5.8, 1.35), P(sxp - 0.55, -5.8, 1.35));
      pushQuad(batches, '#221e38', P(sxp - 0.45, -5.8, 1.35), P(sxp + 0.45, -5.8, 1.35), P(sxp + 0.45, -5.8, 2.15), P(sxp - 0.45, -5.8, 2.15));
      flushQuads(ctx, batches);
      const cone = (cz, cr, hot) => {
        const c = P(sxp, -5.78, cz);
        if (!c) return;
        // square cone on the pixel grid — it breathes in whole-pixel steps
        const r = Math.max(2 * px, Math.round((Math.max(2, (focal * cr) / c[2]) * (1 + pulse * 0.16)) / px) * px);
        const cx = Math.round(c[0] / px) * px, cy = Math.round(c[1] / px) * px;
        ctx.fillStyle = '#141122';
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
        // grill slits: hard horizontal vents every other pixel row
        ctx.fillStyle = '#1d1a30';
        for (let gy = -r + px; gy <= r - 2 * px; gy += 2 * px) ctx.fillRect(cx - r + px, cy + gy, r * 2 - 2 * px, px);
        const m = Math.max(px, Math.round((r * 0.55) / px) * px);
        ctx.fillStyle = hot && pulse > 0.45 ? '#5c5490' : '#332e52';
        ctx.fillRect(cx - m, cy - m, m * 2, m * 2);
        ctx.fillStyle = hot && pulse > 0.6 ? '#9a92d0' : '#514a7c';
        ctx.fillRect(cx, cy, px, px);
        // corner rivets holding the grill on
        ctx.fillStyle = '#0e0c19';
        ctx.fillRect(cx - r, cy - r, px, px);
        ctx.fillRect(cx + r - px, cy - r, px, px);
        ctx.fillRect(cx - r, cy + r - px, px, px);
        ctx.fillRect(cx + r - px, cy + r - px, px, px);
      };
      cone(0.62, 0.34, true); // the woofer takes the kick personally
      cone(1.72, 0.2, false);
      ctx.restore();
    }
  }

  // ---- tonight's DJ: a real online buddy, raised on a riser behind the decks ----
  {
    const dj = w.dj;
    const rb = new Map();
    pushQuad(rb, '#241f3c', P(-1.1, -5.9, 0.55), P(1.1, -5.9, 0.55), P(1.1, -6.6, 0.55), P(-1.1, -6.6, 0.55));
    pushQuad(rb, '#1b1730', P(-1.1, -5.9, 0), P(1.1, -5.9, 0), P(1.1, -5.9, 0.55), P(-1.1, -5.9, 0.55));
    flushQuads(ctx, rb);
    const p = P(0, -6.15, 0.55);
    if (p) {
      const spr = spriteEntry(dj ? `dj:${outfitKey(w, dj.name)}` : 'dj', dj ? dj.seed : DJ_SEED, frame, true, dj && w.outfits?.[dj.name]);
      const k = Math.max(0.4, Math.min(3 * FS, (focal * 1.15) / (p[2] * (SPRITE_H * 2))));
      const dw = Math.round(SPRITE_W * 2 * k);
      const dh = Math.round(SPRITE_H * 2 * k);
      const bob = Math.round(Math.abs(Math.sin(Math.PI * info.beatF)) * 2.4 * k); // head-bobbing to beatPhase
      ctx.drawImage(spr.cv, Math.round(p[0] - dw / 2), Math.round(p[1] - dh - bob), dw, dh);
      if (!backdrop && dj) {
        ctx.font = `${Math.round(7 * FS)}px Silkscreen, monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        const ny = Math.round(p[1] - dh - bob - 3 * px);
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillText(`♪ ${dj.name}`, Math.round(p[0]) + 1, ny + 1);
        ctx.fillStyle = '#f5c542';
        ctx.fillText(`♪ ${dj.name}`, Math.round(p[0]), ny);
        if (dj.bot) {
          // even tonight's dj can be an agent — same dim tag as the floor
          const off = Math.round(ctx.measureText(`♪ ${dj.name}`).width / 2) + 2 * px;
          ctx.font = `${Math.round(5 * FS)}px Silkscreen, monospace`;
          ctx.textAlign = 'left';
          ctx.fillStyle = 'rgba(0,0,0,0.7)';
          ctx.fillText('bot', Math.round(p[0]) + off + 1, ny + 1);
          ctx.fillStyle = '#8b91ad';
          ctx.fillText('bot', Math.round(p[0]) + off, ny);
          ctx.textAlign = 'center';
        }
      }
    }
  }

  // ---- the booth, now an instrument: turntables, mixer, blinkenlights ----
  {
    const fl = P(BOOTH.x0, BOOTH.y, 0), fr = P(BOOTH.x1, BOOTH.y, 0);
    const tr = P(BOOTH.x1, BOOTH.y, BOOTH.z), tl = P(BOOTH.x0, BOOTH.y, BOOTH.z);
    const bl = P(BOOTH.x0, BOOTH.y - BOOTH.top, BOOTH.z), br = P(BOOTH.x1, BOOTH.y - BOOTH.top, BOOTH.z);
    const batches = new Map();
    pushQuad(batches, '#221e38', fl, fr, tr, tl);
    pushQuad(batches, '#2b2647', tl, tr, br, bl);
    flushQuads(ctx, batches);
    if (tl && tr) {
      ctx.fillStyle = pulse > 0.4 ? '#35d07f' : '#1d9e75'; // trim breathes with the kick
      ctx.fillRect(Math.min(tl[0], tr[0]), Math.round(tl[1]), Math.abs(tr[0] - tl[0]), px);
      for (let i = 0; i < 5; i++) {
        // console blinkenlights
        const bp = P(BOOTH.x0 + 0.5 + i * 0.8, BOOTH.y - 0.1, BOOTH.z + 0.02);
        if (!bp) continue;
        ctx.fillStyle = (frame + i) % 3 ? '#f5c542' : '#ff5f7a';
        ctx.fillRect(Math.round(bp[0]), Math.round(bp[1]), px, px);
      }
    }
    // twin turntables — stepped pixel platters that actually spin
    for (const s of [-1.15, 1.15]) {
      const c = P(s, BOOTH.y - 0.38, BOOTH.z + 0.03);
      if (!c) continue;
      const rx = Math.max(2, (focal * 0.34) / c[2]);
      const ry = Math.max(1, rx * 0.4);
      ctx.fillStyle = '#16151f';
      pixelEllipse(ctx, c[0], c[1], rx, ry, px);
      ctx.fillStyle = '#3b3654';
      pixelEllipse(ctx, c[0], c[1], rx * 0.82, ry * 0.82, px);
      ctx.fillStyle = '#16151f';
      pixelEllipse(ctx, c[0], c[1], rx * 0.6, ry * 0.6, px);
      const ang = tt * (3.5 + energy * 3) * (s < 0 ? 1 : -1);
      ctx.fillStyle = '#f5c542';
      ctx.fillRect(Math.round(c[0] + Math.cos(ang) * rx * 0.62) - px, Math.round(c[1] + Math.sin(ang) * ry * 0.62), px * 2, px);
      ctx.fillStyle = '#8b91ad';
      ctx.fillRect(Math.round(c[0]) - px, Math.round(c[1]), px * 2, px);
    }
    // the mixer between them: three fader LEDs bouncing with the beat
    {
      const m = P(0, BOOTH.y - 0.38, BOOTH.z + 0.03);
      if (m) {
        const mw = Math.max(3, (focal * 0.3) / m[2]);
        ctx.fillStyle = '#221f38';
        ctx.fillRect(Math.round(m[0] - mw / 2), Math.round(m[1] - mw * 0.28), Math.round(mw), Math.round(mw * 0.56));
        const cols = ['#35d07f', '#f5c542', '#ff5f7a'];
        for (let j = 0; j < 3; j++) {
          const lv = Math.abs(Math.sin(Math.PI * (info.beatF + j / 3)));
          const lx = Math.round(m[0] - mw / 2 + ((j + 0.5) * mw) / 3);
          const lh = Math.max(px, Math.round(lv * mw * 0.4));
          ctx.fillStyle = cols[j];
          ctx.fillRect(lx, Math.round(m[1] + mw * 0.22) - lh, px, lh);
        }
      }
    }
  }

  // ---- the bar: counter, shelf, bottles catching the light ----
  {
    const batches = new Map();
    pushQuad(batches, '#241f3a', P(-7.55, -3.5, 0), P(-7.55, 0.5, 0), P(-7.55, 0.5, 0.95), P(-7.55, -3.5, 0.95));
    pushQuad(batches, '#332c52', P(-8.35, -3.5, 0.95), P(-7.55, -3.5, 0.95), P(-7.55, 0.5, 0.95), P(-8.35, 0.5, 0.95));
    pushQuad(batches, '#241f3a', P(-8.5, -3.4, 1.75), P(-8.5, 0.4, 1.75), P(-8.5, 0.4, 1.85), P(-8.5, -3.4, 1.85));
    flushQuads(ctx, batches);
    const t0 = P(-7.55, -3.5, 0.95), t1 = P(-7.55, 0.5, 0.95);
    if (t0 && t1) {
      ctx.strokeStyle = pulse > 0.5 ? '#f5c542' : '#8a6f2a';
      ctx.lineWidth = px;
      ctx.beginPath();
      ctx.moveTo(t0[0], t0[1]);
      ctx.lineTo(t1[0], t1[1]);
      ctx.stroke();
    }
    const bcols = ['#35d07f', '#f5c542', '#6fb7e8', '#ff5f7a', '#b18aff', '#9fe1cb'];
    for (let i = 0; i < 6; i++) {
      const by = -3.15 + i * 0.64;
      const a = P(-8.48, by, 1.85), b = P(-8.48, by, 2.25);
      if (!a || !b) continue;
      const bwp = Math.max(px, Math.round((focal * 0.07) / a[2]));
      ctx.fillStyle = bcols[i];
      ctx.fillRect(Math.round(a[0] - bwp / 2), Math.round(b[1]), bwp, Math.max(px, Math.round(a[1] - b[1])));
      if ((frame + i * 2) % 9 === 0) {
        ctx.fillStyle = '#f4f6ff';
        ctx.fillRect(Math.round(a[0] - bwp / 2), Math.round(b[1]), px, px);
      }
    }
  }

  // ---- 卡座: booth seats along the walls, candle-lit tables ----
  for (let si = 0; si < SEATS.length; si++) {
    const st = SEATS[si];
    const wx = st.x, s = st.s;
    const batches = new Map();
    pushQuad(batches, '#3a2f55', P(wx + 0.45 * s, st.y - 0.95, 0.35), P(wx + 0.45 * s, st.y + 0.95, 0.35), P(wx + 0.45 * s, st.y + 0.95, 1.15), P(wx + 0.45 * s, st.y - 0.95, 1.15));
    pushQuad(batches, '#4a3a68', P(wx - 0.25 * s, st.y - 0.95, 0.45), P(wx + 0.45 * s, st.y - 0.95, 0.45), P(wx + 0.45 * s, st.y + 0.95, 0.45), P(wx - 0.25 * s, st.y + 0.95, 0.45));
    pushQuad(batches, '#332a4e', P(wx - 0.25 * s, st.y - 0.95, 0), P(wx - 0.25 * s, st.y + 0.95, 0), P(wx - 0.25 * s, st.y + 0.95, 0.45), P(wx - 0.25 * s, st.y - 0.95, 0.45));
    const tx = wx - 1.25 * s;
    pushQuad(batches, '#332c52', P(tx - 0.42, st.y - 0.42, 0.62), P(tx + 0.42, st.y - 0.42, 0.62), P(tx + 0.42, st.y + 0.42, 0.62), P(tx - 0.42, st.y + 0.42, 0.62));
    flushQuads(ctx, batches);
    const leg0 = P(tx, st.y, 0), leg1 = P(tx, st.y, 0.62);
    if (leg0 && leg1) {
      ctx.strokeStyle = '#241f3a';
      ctx.lineWidth = px;
      ctx.beginPath();
      ctx.moveTo(leg0[0], leg0[1]);
      ctx.lineTo(leg1[0], leg1[1]);
      ctx.stroke();
    }
    const cd = P(tx, st.y, 0.68);
    if (cd) {
      ctx.fillStyle = frame % 2 ? '#f5c542' : '#ffdf8a';
      ctx.fillRect(Math.round(cd[0]), Math.round(cd[1]) - px, px, px);
    }
    // when the floor is packed, the overflow crowd takes the seats
    const name = w.seated[si];
    if (name) {
      const sp = P(wx + 0.05 * s, st.y, 0.45);
      if (sp) {
        const spr = spriteEntry(outfitKey(w, name), petSeedFor(name), frame, false, w.outfits?.[name]);
        const k2 = Math.max(0.3, Math.min(3 * FS, (focal * 0.95) / (sp[2] * (SPRITE_H * 2))));
        const dw2 = Math.round(SPRITE_W * 2 * k2);
        const dh2 = Math.round(SPRITE_H * 2 * k2);
        ctx.drawImage(spr.cv, Math.round(sp[0] - dw2 / 2), Math.round(sp[1] - dh2), dw2, dh2);
      }
    }
  }

  // ---- dancers: depth-sorted billboards, names overhead ----
  {
    const list = [];
    for (const d of w.dancers.values()) {
      const p = P(d.x, d.y, 0);
      if (p) list.push({ d, p });
    }
    list.sort((a, b) => b.p[2] - a.p[2]);
    for (const { d, p } of list) {
      const k = Math.max(0.35, Math.min(3 * FS, (focal * 1.1) / (p[2] * (SPRITE_H * 2))));
      const dw = Math.round(SPRITE_W * 2 * k);
      const dh = Math.round(SPRITE_H * 2 * k);
      const beatT = info.beatF + d.phase;
      const amp = (1.5 + energy * 2.5) * k;
      const hop = Math.round(Math.abs(Math.sin(Math.PI * beatT)) * amp);
      let dx = 0;
      let flip = false;
      if (d.style === 1) dx = Math.round(Math.sin(Math.PI * beatT) * 2.5 * k); // side-steps
      if (d.style === 2) flip = Math.floor(beatT) % 2 === 0; // spins on the beat
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      pixelEllipse(ctx, p[0], p[1] + px, Math.max(2 * px, dw * 0.32), Math.max(px, dw * 0.1), px);
      const spr = spriteEntry(outfitKey(w, d.name), petSeedFor(d.name), frame, false, w.outfits?.[d.name]);
      ctx.drawImage(flip ? spr.flip : spr.cv, Math.round(p[0] - dw / 2 + dx), Math.round(p[1] - dh - hop), dw, dh);
      if (!backdrop) {
        const big = k > 1.3 * FS;
        ctx.font = `${Math.round((big ? 10 : 7) * FS)}px Silkscreen, monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        const ny = Math.round(p[1] - dh - hop - 3 * px);
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillText(d.name, Math.round(p[0]) + 1, ny + 1);
        ctx.fillStyle = nameColor(d.name);
        ctx.fillText(d.name, Math.round(p[0]), ny);
        if (d.bot) {
          // agents dance too — a dim little 'bot' rides the end of the name
          const off = Math.round(ctx.measureText(d.name).width / 2) + 2 * px;
          ctx.font = `${Math.round((big ? 7 : 5) * FS)}px Silkscreen, monospace`;
          ctx.textAlign = 'left';
          ctx.fillStyle = 'rgba(0,0,0,0.7)';
          ctx.fillText('bot', Math.round(p[0]) + off + 1, ny + 1);
          ctx.fillStyle = '#8b91ad';
          ctx.fillText('bot', Math.round(p[0]) + off, ny);
          ctx.textAlign = 'center';
        }
      }
    }
  }

  // ---- the disco ball: square facets on the pixel grid, stepped highlight ----
  {
    const p = P(BALL[0], BALL[1], BALL[2]);
    if (p) {
      const top = P(BALL[0], BALL[1], 4.8);
      const r = Math.max(2, (focal * 0.4) / p[2]);
      if (top) {
        ctx.fillStyle = '#3a3654';
        ctx.fillRect(Math.round(p[0]), Math.round(Math.min(top[1], p[1] - r)), px, Math.round(Math.abs(p[1] - r - top[1])) + 1);
      }
      // facet cell: ~4 a side, always a whole multiple of the pixel unit —
      // the silhouette staircases, never rounds
      const fc = Math.max(px, Math.round(r / (4 * px)) * px || px);
      const cx = Math.round(p[0] / fc) * fc, cy = Math.round(p[1] / fc) * fc;
      const n = Math.max(2, Math.round(r / fc));
      const spin = Math.floor(tt * 6); // facet columns crawl in whole-cell steps
      for (let gy = -n; gy < n; gy++) {
        const ty = (gy + 0.5) / n;
        const half = Math.floor(n * Math.sqrt(Math.max(0, 1 - ty * ty)));
        for (let gx = -half; gx < half; gx++) {
          const chk = (gx + gy + spin) & 1; // mirror tiles marching around the ball
          const diag = gx + gy; // the light sits up-left: banded, not blended
          ctx.fillStyle = diag < -n ? (chk ? '#aab0cc' : '#8b91ad')
            : diag > n - 2 ? (chk ? '#6a708c' : '#4e4a6a')
            : chk ? '#8b91ad' : '#6a708c';
          ctx.fillRect(cx + gx * fc, cy + gy * fc, fc, fc);
        }
      }
      ctx.fillStyle = '#f4f6ff';
      for (let i = 0; i < 5; i++) {
        const a = tt * 2.7 + i * 1.26;
        if (Math.cos(a) < -0.1) continue; // glints only on the lit face
        // glints land on facet corners — whole cells, no subpixel sparkle
        ctx.fillRect(cx + Math.round((Math.sin(a) * r * 0.7) / fc) * fc, cy + Math.round((Math.sin(a * 0.7 + i) * r * 0.45) / fc) * fc, fc, fc);
      }
    }
  }

  // ---- the beams themselves: pixel staircases ending exactly on their pools ----
  ctx.globalCompositeOperation = 'screen';
  for (const b of beams) {
    const a = P(b.x, -5.0, 4.55);
    const l = P(b.fx - b.w / 2, b.fy, 0);
    const r = P(b.fx + b.w / 2, b.fy, 0);
    if (!a || !l || !r) continue;
    ctx.fillStyle = b.color;
    const aOut = qa(b.alpha), aCore = qa(Math.min(0.5, b.alpha * 1.6));
    // horizontal slabs from truss to floor: hard-edged steps, a brighter core band
    const y0 = a[1], y1 = (l[1] + r[1]) / 2;
    const steps = Math.max(3, Math.min(14, Math.round(Math.abs(y1 - y0) / (6 * px))));
    for (let i = 0; i < steps; i++) {
      const yA = Math.round(lerp(y0, y1, i / steps)), yB = Math.round(lerp(y0, y1, (i + 1) / steps));
      const yT = Math.min(yA, yB), hh = Math.max(1, Math.abs(yB - yA));
      const xl = Math.round(lerp(a[0] - px, l[0], i / steps) / px) * px;
      const xr = Math.round(lerp(a[0] + px, r[0], i / steps) / px) * px;
      const x0 = Math.min(xl, xr), ww = Math.max(px, Math.abs(xr - xl));
      ctx.globalAlpha = aOut;
      ctx.fillRect(x0, yT, ww, hh);
      ctx.globalAlpha = aCore;
      const cw = Math.max(px, Math.round((ww * 0.22) / px) * px);
      ctx.fillRect(Math.round((x0 + ww / 2 - cw / 2) / px) * px, yT, cw, hh);
    }
  }
  ctx.globalAlpha = 1;
  if (energy < 0.9) {
    // moonlit: slow color washes · velvet: a low smoky drift — four hard bands,
    // hue steps in 15° notches, no gradient anywhere
    const hue = (Math.round((tt * (energy < 0.5 ? 9 : 5)) / 15) * 15) % 360;
    const sat = energy < 0.5 ? 70 : 40;
    const bh = Math.ceil(H / 4);
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = `hsla(${(hue + i * 40) % 360} ${sat}% ${60 - i * 2}% / ${WASH_ALPHA[i]})`;
      ctx.fillRect(0, i * bh, W, bh);
    }
  }
  if (energy >= 0.9 && info.beatPhase < 0.08 && info.bar % 8 !== 7) {
    // voltage: the strobe pops on every kick (and holds its breath for the riser)
    ctx.fillStyle = 'rgba(255,255,255,0.16)';
    ctx.fillRect(0, 0, W, H);
  }
  ctx.globalCompositeOperation = 'source-over';

  // ---- speech bubbles: the ambience layer (never on the backdrop) ----
  if (!backdrop) {
    while (w.bubbles.length < 4 && w.queue.length) w.bubbles.push({ ...w.queue.shift(), t0: tt });
    w.bubbles = w.bubbles.filter((b) => tt - b.t0 < 6);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    for (const b of w.bubbles) {
      const age = tt - b.t0;
      const p = P(b.wx, b.wy, 1.9 + age * 0.28);
      if (!p) continue;
      ctx.font = `${7 * px}px Silkscreen, monospace`;
      let nw = Math.ceil(ctx.measureText(b.name).width);
      if (b.bot) {
        // the author line grows a dim 'bot' tag — the pill has to fit it
        ctx.font = `${5 * px}px Silkscreen, monospace`;
        nw += 3 * px + Math.ceil(ctx.measureText('bot').width);
        ctx.font = `${7 * px}px Silkscreen, monospace`;
      }
      const tw = b.emote ? 24 * px : Math.ceil(ctx.measureText(b.text).width);
      const bw = Math.max(nw, tw) + 10 * px;
      const bh = b.emote ? 42 * px : 24 * px;
      const x = Math.round(Math.max(2, Math.min(W - bw - 2, p[0] - bw / 2)));
      const y = Math.round(Math.max(2, Math.min(H - bh - 6 * px, p[1] - bh)));
      ctx.globalAlpha = age < 4.5 ? 0.95 : qa(Math.max(0, (6 - age) / 1.5)); // fade in steps
      ctx.fillStyle = '#3b3654';
      ctx.fillRect(x - px, y - px, bw + 2 * px, bh + 2 * px);
      ctx.fillStyle = 'rgba(22,20,34,0.94)';
      ctx.fillRect(x, y, bw, bh);
      ctx.fillStyle = '#3b3654';
      ctx.fillRect(Math.round(p[0]) - px, y + bh + px, 2 * px, 3 * px);
      ctx.fillStyle = b.color;
      ctx.fillText(b.name, x + 5 * px, y + 4 * px);
      if (b.bot) {
        const bo = Math.ceil(ctx.measureText(b.name).width) + 3 * px;
        ctx.font = `${5 * px}px Silkscreen, monospace`;
        ctx.fillStyle = '#6a708c';
        ctx.fillText('bot', x + 5 * px + bo, y + 5 * px);
        ctx.font = `${7 * px}px Silkscreen, monospace`;
      }
      if (b.emote) {
        const em = EMOTES[b.emote];
        for (let ey = 0; ey < 12; ey++)
          for (let ex = 0; ex < 12; ex++) {
            const v = em.g[ey][ex];
            if (v === '.') continue;
            ctx.fillStyle = em.c[Number(v) - 1];
            ctx.fillRect(x + Math.round(bw / 2) - 12 * px + ex * 2 * px, y + 15 * px + ey * 2 * px, 2 * px, 2 * px);
          }
      } else {
        ctx.fillStyle = '#e8e4f4';
        ctx.fillText(b.text, x + 5 * px, y + 13 * px);
      }
      ctx.globalAlpha = 1;
    }
  }
}

// ---------------------------------------------------------------------------
// PERSONAL SCROLLBACK — IndexedDB `vb-lobby-<user>`: 7 days / 500 messages.
// The server only keeps an in-memory ring; reloads read history from here.
// ---------------------------------------------------------------------------

const lobbyDbs = new Map();
function lobbyStore(user) {
  if (!user) return Promise.reject(new Error('no user'));
  if (!lobbyDbs.has(user)) {
    const p = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('no idb'));
      const req = indexedDB.open(`vb-lobby-${user}`, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains('msgs')) req.result.createObjectStore('msgs', { keyPath: 'k' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    p.catch(() => lobbyDbs.delete(user)); // a failed open must not poison retries
    lobbyDbs.set(user, p);
  }
  return lobbyDbs.get(user);
}

async function lobbyOp(user, mode, run) {
  const d = await lobbyStore(user);
  return new Promise((resolve, reject) => {
    const t = d.transaction('msgs', mode);
    const out = run(t.objectStore('msgs'));
    t.oncomplete = () => resolve(out?.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const msgKey = (m) => `${m.ts}:${m.from}:${(hashString(String(m.body)) % 1e6).toString(36)}`;
const lobbyPut = (user, rec) => lobbyOp(user, 'readwrite', (s) => s.put(rec));

async function lobbyLoad(user) {
  try {
    const all = (await lobbyOp(user, 'readonly', (s) => s.getAll())) ?? [];
    all.sort((a, b) => a.ts - b.ts);
    const cutoff = Date.now() / 1000 - 7 * 86400;
    const keep = all.filter((m) => m.ts >= cutoff).slice(-500);
    if (keep.length !== all.length) {
      const dead = new Set(all.map((m) => m.k));
      for (const m of keep) dead.delete(m.k);
      lobbyOp(user, 'readwrite', (s) => {
        for (const k of dead) s.delete(k);
      }).catch(() => {});
    }
    return keep;
  } catch {
    return [];
  }
}

const SOLO_EMOTE = /^:([a-z0-9_-]{2,24}):$/;

function spawnBubble(w, rec) {
  const d = w.dancers.get(rec.from);
  const spot = SPOTS[hashString(`bub:${rec.from}`) % SPOTS.length];
  const em = String(rec.body).trim().match(SOLO_EMOTE);
  w.queue.push({
    name: rec.from,
    bot: !!rec.bot,
    color: nameColor(rec.from),
    emote: em && EMOTES[em[1]] ? em[1] : null,
    text: rec.body.length > 24 ? `${rec.body.slice(0, 23)}…` : rec.body,
    wx: d ? d.x : spot[0],
    wy: d ? d.y : spot[1],
  });
  if (w.queue.length > 8) w.queue.shift(); // a flood stays an ambience, not a wall
}

const hhmm = (ts) => {
  const d = new Date((ts || 0) * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ---- hand-set 12x12 pixel icon for the club-off header control ('2' = accent) ----
// (mute + track lock moved to the statusbar music pill — this is the only chip left)

const ICON_OFF = [
  '.....##.....',
  '.....##...2.',
  '...######2..',
  '..#.####2#..',
  '..#####2##..',
  '..#.##2#.#..',
  '..###2####..',
  '...#2####...',
  '...2####....',
  '..2.........',
  '.2..........',
  '............',
];

function PixIcon({ grid, accent = '#ff5f7a' }) {
  const rects = [];
  grid.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] === '.') continue;
      rects.push(html`<rect x=${x * 2} y=${y * 2} width="2" height="2" fill=${row[x] === '2' ? accent : 'currentColor'} />`);
    }
  });
  return html`<svg width="24" height="24" viewBox="0 0 24 24" shape-rendering="crispEdges" aria-hidden="true">${rects}</svg>`;
}

// ---------------------------------------------------------------------------
// <ClubLobby> — the lobby tab's whole body (or, with `backdrop`, a dim film
// running behind the table overlay: no bubbles, no chrome, music continues).
// ---------------------------------------------------------------------------

export function ClubLobby({ ws, msgs = [], online = 0, roster = [], me, lastShip, tableCall,
  onProfile, onRoll, onOff, backdrop = false }) {
  const canvasRef = useRef(null);
  const worldRef = useRef(null);
  const drawerRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [size, setSize] = useState(3);
  const [history, setHistory] = useState([]);

  if (!worldRef.current)
    worldRef.current = {
      frame: 0, dancers: new Map(), bubbles: [], queue: [], seen: null,
      seed: 1, ms: 0, led: new Array(LW * LH).fill('#0d1124'),
      shot: null, shotStart: 0, shotQueue: [], shotRnd: null, shotEpoch: 0, lastGbar: -1,
      dj: null, seated: [],
    };

  useEffect(() => {
    worldRef.current.seed = hashString(`club:${me?.username ?? 'porch'}`);
  }, [me?.username]);

  // the jukebox is app-global — the lobby only nudges it awake; mute and the
  // track lock live on the statusbar pill now. unmount stops NOTHING.
  useEffect(() => {
    music.start();
  }, []);

  // the projector: ~11fps, cost measured (window.__vbClubMs, EMA).
  // each tick starts by fitting the internal resolution to the on-screen region
  // at an integer pixel scale — full bleed, aspect-true, no letterbox ever
  useEffect(() => {
    const iv = setInterval(() => {
      const cv = canvasRef.current;
      if (!cv) return;
      if (!backdrop && cv.parentElement) {
        const cw = Math.max(1, cv.parentElement.clientWidth);
        const ch = Math.max(1, cv.parentElement.clientHeight);
        const S = Math.max(1, Math.round(cw / 460)); // internal width lands ~400-550
        const iw = Math.max(160, Math.floor(cw / S));
        const ih = Math.max(120, Math.floor(ch / S));
        if (cv.width !== iw || cv.height !== ih) {
          cv.width = iw;
          cv.height = ih;
        }
      }
      const t1 = performance.now();
      drawClub(cv, worldRef.current, backdrop);
      const w = worldRef.current;
      w.ms = w.ms * 0.9 + (performance.now() - t1) * 0.1;
      window.__vbClubMs = w.ms;
    }, TICK_MS);
    return () => clearInterval(iv);
  }, [backdrop]);

  // presence who is [{ username, bot }] now — normalize (plain-string frames can
  // still land mid-deploy) and remember which faces are bots for the tiny tag.
  // objects would join('|') into '[object Object]', so the dep key is hand-rolled
  const rosterKey = roster
    .map((r) => (typeof r === 'string' ? r : `${r?.username ?? ''}${r?.bot ? '~b' : ''}`))
    .join('|');

  // the crowd: up to 20 online buddies, stable slots while present, owner always
  // in; one of them is elected tonight's DJ (same pick on every client)
  useEffect(() => {
    const w = worldRef.current;
    const entries = roster.map((r) => (typeof r === 'string' ? { username: r, bot: false } : r ?? {}));
    const bots = new Set(entries.filter((e) => e.bot).map((e) => e.username));
    const names = [...new Set(entries.map((e) => e.username).filter(Boolean))];
    const mine = me?.username;
    const sorted = names.slice().sort((a, b) => hashString(`club:${a}`) - hashString(`club:${b}`));
    const day = Math.floor(Date.now() / 86400000);
    const djName = names.length ? [...names].sort()[hashString(`dj:${day}`) % names.length] : null;
    w.dj = djName ? { name: djName, seed: petSeedFor(djName), bot: bots.has(djName) } : null;
    const pick = mine && names.includes(mine) && mine !== djName ? [mine] : [];
    for (const n of sorted) {
      if (pick.length >= 20) break;
      if (n !== mine && n !== djName) pick.push(n);
    }
    const keep = new Set(pick);
    for (const n of [...w.dancers.keys()]) if (!keep.has(n)) w.dancers.delete(n);
    const used = new Set([...w.dancers.values()].map((d) => d.slot));
    for (const n of pick) {
      const have = w.dancers.get(n);
      if (have) {
        have.bot = bots.has(n); // a held slot still refreshes its tag
        continue;
      }
      let slot = hashString(`slot:${n}`) % SPOTS.length;
      while (used.has(slot)) slot = (slot + 1) % SPOTS.length;
      used.add(slot);
      const r = mulberry32(hashString(`dance:${n}`));
      w.dancers.set(n, {
        name: n, slot, bot: bots.has(n),
        x: SPOTS[slot][0] + (r() - 0.5) * 0.7,
        y: SPOTS[slot][1] + (r() - 0.5) * 0.7,
        style: hashString(`style:${n}`) % 3,
        phase: r(),
      });
    }
    // past the dancer cap, the overflow crowd takes the booth seats
    w.seated = sorted.filter((n) => !keep.has(n) && n !== djName).slice(0, SEATS.length);
    // everyone in the room wears their real outfit — one batch fetch per roster change
    const everyone = [...new Set([...(djName ? [djName] : []), ...pick, ...w.seated])];
    if (everyone.length)
      fetch(`/api/outfits?users=${encodeURIComponent(everyone.join(','))}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.outfits) w.outfits = d.outfits;
        })
        .catch(() => {});
  }, [rosterKey, me?.username]);

  // scrollback in, new messages out: persist everything, bubble only the live ones
  useEffect(() => {
    if (backdrop || !me?.username) return;
    let alive = true;
    lobbyLoad(me.username).then((h) => alive && setHistory(h));
    return () => {
      alive = false;
    };
  }, [me?.username, backdrop]);

  useEffect(() => {
    const w = worldRef.current;
    const first = !w.seen; // the hello backlog is history, not a bubble parade
    if (!w.seen) w.seen = new Set();
    for (const m of msgs) {
      // lobby from is { username, bot } now — the flag rides into scrollback too
      const rec = { ts: m.ts ?? 0, from: m.from?.username ?? '?', bot: !!m.from?.bot, body: String(m.body ?? '') };
      const k = msgKey(rec);
      if (w.seen.has(k)) continue;
      w.seen.add(k);
      if (me?.username) lobbyPut(me.username, { k, ...rec }).catch(() => {});
      if (!first && !backdrop) spawnBubble(w, rec);
    }
  }, [msgs]);

  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of history)
      if (!seen.has(m.k)) {
        seen.add(m.k);
        out.push(m);
      }
    for (const m of msgs) {
      const rec = { ts: m.ts ?? 0, from: m.from?.username ?? '?', bot: !!m.from?.bot, body: String(m.body ?? '') };
      rec.k = msgKey(rec);
      if (!seen.has(rec.k)) {
        seen.add(rec.k);
        out.push(rec);
      }
    }
    out.sort((a, b) => a.ts - b.ts);
    return out.slice(-200);
  }, [history, msgs]);

  useEffect(() => {
    const el = drawerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [drawerOpen, merged.length]);

  const send = () => {
    const body = draft.trim();
    if (!body || !ws) return;
    ws.send(JSON.stringify({ t: 'lobby_send', body }));
    setDraft('');
  };

  if (backdrop)
    return html`<div style="position:absolute;inset:0;overflow:hidden;pointer-events:none;opacity:.4">
      <canvas ref=${canvasRef} class="pixel" width="272" height="208"
        style="width:100%;height:100%;object-fit:cover"></canvas>
    </div>`;

  const last = merged[merged.length - 1];

  return html`
    <div class="roomhead" style="display:flex;align-items:center;gap:8px">
      <span>🪩 the club · ${online} here</span>
      <span style="margin-left:auto;display:flex;align-items:center;gap:6px;flex-shrink:0">
        <button class="mini" style="padding:2px 4px;line-height:0" title="lights up — back to the plain lobby" onClick=${() => onOff?.()}>
          <${PixIcon} grid=${ICON_OFF} />
        </button>
      </span>
    </div>
    ${lastShip &&
    html`<div class="shipticker">
      ⛵ <b class="clickable" onClick=${() => onProfile?.(lastShip.username)}>${lastShip.username}</b>
      shipped: ${lastShip.title}
      ${lastShip.adds != null && html`<span class="adds">+${lastShip.adds}</span>`}
      ${lastShip.dels != null && html`<span class="dels">−${lastShip.dels}</span>`}
    </div>`}
    ${tableCall &&
    html`<div class="tablecall">
      🕯 <b class="clickable" onClick=${() => onProfile?.(tableCall.from)}>${tableCall.from}</b>
      waits at a table for ${tableCall.size} — ${tableCall.seats_open} seat${tableCall.seats_open > 1 ? 's' : ''} open
      <button class="mini primary" onClick=${() => onRoll?.(tableCall.size)}>join</button>
    </div>`}
    <div class="tablebar">
      <button class="mini primary" onClick=${() => onRoll?.(size)}>🎲 table for ${size}</button>
      <span class="sizes">
        ${[2, 3, 4].map(
          (n) => html`<span class="sizeopt ${size === n ? 'on' : ''}" onClick=${() => setSize(n)}>${n}</span>`
        )}
      </span>
    </div>
    <div style="position:relative;flex:1;min-height:240px;overflow:hidden;background:#0b0a12">
      <canvas ref=${canvasRef} class="pixel" width="272" height="208"
        style="width:100%;height:100%;display:block"></canvas>
    </div>
    ${!drawerOpen &&
    html`<div class="clickable" style="border-top:1px solid var(--border);padding:5px 14px;font-family:var(--font-pixel);font-size:11px;color:var(--text-dim);white-space:nowrap;overflow:hidden;text-overflow:ellipsis"
      onClick=${() => setDrawerOpen(true)}>
      ${last
        ? html`<b style="color:${nameColor(last.from)}">${last.from}</b>${last.bot && html` <span class="botpill">bot</span>`} ${last.body} <span class="dim">▴</span>`
        : html`quiet in here… say something <span class="dim">▴</span>`}
    </div>`}
    ${drawerOpen &&
    html`<div style="border-top:1px solid var(--border);background:var(--surface);display:flex;flex-direction:column;flex:0 1 auto;min-height:0;max-height:45%">
      <div style="display:flex;padding:5px 14px 0;font-family:var(--font-pixel);font-size:10px;color:var(--text-faint)">
        <span>the night so far · last 7 days live here</span>
        <span class="clickable" style="margin-left:auto" onClick=${() => setDrawerOpen(false)}>▾ close</span>
      </div>
      <div ref=${drawerRef} style="overflow-y:auto;min-height:0;padding:6px 14px 8px;display:flex;flex-direction:column;gap:4px">
        ${merged.length === 0 && html`<div class="dim" style="font-size:12px">no chatter yet tonight</div>`}
        ${merged.map(
          (m) => html`<div style="font-size:13px;line-height:1.35">
            <b class="clickable" style="color:${nameColor(m.from)};font-family:var(--font-pixel);font-size:11px"
              onClick=${() => m.from !== me?.username && onProfile?.(m.from)}>${m.from}</b>
            ${m.bot && html`<span class="botpill">bot</span>`}
            <span style="color:var(--text-dim)"> ${m.body}</span>
            <span class="dim" style="font-size:9px;font-family:var(--font-pixel)"> ${hhmm(m.ts)}</span>
          </div>`
        )}
      </div>
    </div>`}
    <div class="composer">
      <input type="text" maxlength="200" placeholder="say hi…" value=${draft}
        onInput=${(e) => setDraft(e.target.value)} onKeyDown=${(e) => e.key === 'Enter' && send()} />
      <button onClick=${send}>send</button>
    </div>
  `;
}
